/**
 * The Digital Council's queue (E4.7, #1293) — what we routed to them.
 *
 * There was a per-browser "last looked" marker here (an unread split for the
 * page). Removed 2026-09-03 with the flat-list decision: the audience knows
 * the SharePoint-list experience, and that experience is a plain grid.
 *
 * Nothing here invalidates or is invalidated by the demand caches: the queue
 * is its own contract (`/digital-council/queue`), written for a reader outside
 * this application, and it changes only when something is routed — which goes
 * through `useRouteDemand`, whose invalidation already covers this key via the
 * prefix below being registered in INVALIDATED_PREFIXES.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'

export interface RoutedRequest {
  demand_id: number
  title: string
  description: string
  submitter_name?: string | null
  submitter_email?: string | null
  department?: string | null
  submitted_at?: string | null
  routed_at?: string | null
  routed_by?: string | null
  /** Why we think it is theirs — recorded when someone routed it. The point
   *  of the page: it answers "why is this in front of me". */
  reason?: string | null
  /** Who in DSAI has it — see components/DsaiAssignment. */
  dsai_cluster?: string | null
  dsai_cluster_label?: string | null
  caretaker_id?: number | null
  caretaker_name?: string | null
  /** The server's answer: may this caller assign cluster and caretaker? */
  can_assign?: boolean
  /** Where DSAI is with it. Provisional values, from GET /digital-council/statuses. */
  dsai_status?: string
  dsai_status_label?: string
  comment_count?: number
}

export interface DsaiStatusOption {
  code: string
  label: string
}

export interface DsaiComment {
  id: number
  author_name?: string | null
  body: string
  created_at: string
}

export interface DsaiCluster {
  code: string
  name: string
  lead: string
  /** "Data (Kerstin)" */
  label: string
}

export interface RoutedQueueResponse {
  items: RoutedRequest[]
  total: number
}

export const DIGITAL_COUNCIL_QUEUE_KEY = ['digital-council-queue'] as const

/** The DSAI clusters, from the server's registry — the frontend keeps no
 *  copy. They change about as often as the org chart, hence the long cache. */
export function useDsaiClusters() {
  return useQuery({
    queryKey: ['dsai-clusters'],
    queryFn: async () => (await api.get('/digital-council/clusters')).data as DsaiCluster[],
    staleTime: 60 * 60 * 1000,
  })
}

export function useDigitalCouncilQueue() {
  return useQuery({
    queryKey: DIGITAL_COUNCIL_QUEUE_KEY,
    queryFn: async () => {
      const res = await api.get('/digital-council/queue')
      return res.data as RoutedQueueResponse
    },
  })
}


/** The status values, in the order they are offered. */
export function useDsaiStatuses() {
  return useQuery({
    queryKey: ['dsai-statuses'],
    queryFn: async () => (await api.get('/digital-council/statuses')).data as DsaiStatusOption[],
    staleTime: 60 * 60 * 1000,
  })
}

export function useSetDsaiStatus(demandId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (status: string) =>
      (await api.put(`/digital-council/${demandId}/status`, { status })).data as DsaiStatusOption,
    onSuccess: () => qc.invalidateQueries({ queryKey: DIGITAL_COUNCIL_QUEUE_KEY }),
  })
}

export function useDsaiComments(demandId: number) {
  return useQuery({
    queryKey: ['dsai-comments', demandId],
    queryFn: async () =>
      (await api.get(`/digital-council/${demandId}/comments`)).data as DsaiComment[],
  })
}

export function useAddDsaiComment(demandId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: string) =>
      (await api.post(`/digital-council/${demandId}/comments`, { body })).data as DsaiComment,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dsai-comments', demandId] })
      qc.invalidateQueries({ queryKey: DIGITAL_COUNCIL_QUEUE_KEY })
    },
  })
}
